import { useEffect, useRef, type ComponentPropsWithoutRef } from 'react';
import { useT } from '../lib/idioma.js';
import { Link } from 'react-router-dom';

import { caminhoDaArea } from '../lib/endereco.js';
import {
  destinoDaTranca,
  medidasDaTranca,
  partesDaFrase,
  SEM_MOTIVO,
  type MedidaDaTranca,
  type TrancaDeOrdem,
} from '../lib/tranca.js';

/**
 * ── O CADEADO DE ORDEM: O QUE ELE AFIRMA, E O QUE RECUSA ────────────────────
 *
 * **A tese.** Um curso que espera outro não está fechado — está *em fila*. O
 * desenho tem de dizer três coisas antes do clique: que não abre agora, o que o
 * abre, e **quanto falta**. Recusa parecer-se com «você não comprou»: ali o
 * cadeado é um convite à venda, e o aluno confunde os dois no primeiro olhar.
 * Por isso a vitrine continua a dizer *Você ainda não tem* em `--destaque`, sem
 * cadeado nenhum, e o cadeado desenhado fica sendo só desta espécie.
 *
 * **A signature.** A **régua da tranca**: um fio de 3px com uma marca no mínimo
 * que o tutor pede e o preenchimento onde a pessoa está. É a única peça nova, e
 * é a mesma nas quatro telas. Ela existe porque «pede 60% — você está em 25%» é
 * uma subtração que ninguém devia ter de fazer a olho, e porque a distância
 * desenhada é o que transforma uma recusa num percurso.
 *
 * **O que fica quieto para ela funcionar.** O cartaz perde o levantar do hover,
 * porque deixou de ser um link. (A frase que estava aqui — *«o cartaz não ganha
 * véu, nem cor, nem tarja»* — **caiu por ordem do dono em 24/09/2026**; ver
 * `VeuDoCartaz`, abaixo, que guarda o argumento antigo e a razão de ele ter
 * perdido.) Não há
 * badge de estado por baixo do título — o selo no canto do cartaz já o diz uma
 * vez. E não há botão «Abrir o curso X» ao lado da frase: **o nome dentro da
 * frase é o link**, porque escrever o mesmo nome duas vezes a 8px de distância
 * é o acessório que se tira antes de sair de casa.
 *
 * ## Onde o nome DEIXOU de ser o link, e por que isso não abre um beco
 *
 * Na **capa da prateleira**, desde 14/10/2026 à tarde: lá o cartaz inteiro
 * passou a ser o gesto — um `<button>` que abre `AvisoDaTranca` — e um `<a>`
 * dentro de um `<button>` é nesting ilegal antes de ser mau desenho. A saída
 * do cadeado continua a existir, uma camada mais funda: a do diálogo, que é um
 * alvo de 44px e **nomeia** o que falta. É lá, e só lá, que o nome aparece duas
 * vezes — e aí ele paga-se, porque um «Ir para o curso» com dois cursos
 * nomeados na mesma caixa não diz qual. Ver `MotivoNoCartaz` e `AvisoDaTranca`.
 *
 * ## Por que nada aqui é vermelho
 *
 * Porque não é erro nem aviso: é o estado normal de quem ainda não chegou. A
 * cor da régua é `--destaque`, a mesma de todo progresso desta área — o que ela
 * mostra é o avanço da pessoa, e é dela que a frase está a falar.
 */

/**
 * O cadeado, desenhado — e nunca um emoji.
 *
 * Pela mesma razão da sala (`a-sala-diz-o-que-e-cada-linha`): emoji muda de
 * forma, de peso e de cor em cada sistema, e este símbolo aparece em quatro
 * telas que têm de parecer a mesma. `currentColor` para herdar a tinta de onde
 * estiver, e `aria-hidden` porque a frase ao lado já diz tudo por extenso.
 */
export function Cadeado({ tamanho = 12 }: { tamanho?: number }) {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      aria-hidden
      focusable="false"
    >
      <rect x="2" y="5.2" width="8" height="5.6" rx="1" />
      <path d="M4 5.2V3.8a2 2 0 0 1 4 0v1.4" />
    </svg>
  );
}

/**
 * A chapa no canto do cartaz — o lugar, não o que está escrito nele.
 *
 * Fica **sobre a arte** porque o cartaz é o que a pessoa vê a três metros de
 * distância, e é lá que a pergunta «posso abrir?» é feita. O fundo é a cor de
 * fundo DA ÁREA a 82%: lê-se sobre capa clara e sobre capa escura sem inventar
 * uma cor que não é do cliente.
 *
 * É um componente à parte desde 24/09/2026, quando o curso ganhou **data de
 * estreia** e apareceu um segundo carimbo no mesmo canto (`SeloDeLancamento`).
 * O que os dois partilham é a chapa; o que os distingue é o glifo e a palavra —
 * e é assim que se quer, porque a chapa igual é o que diz «este cartaz não
 * abre» de longe, e o conteúdo é o que diz **por quê** de perto. Escrever o
 * mesmo `span` duas vezes faria as duas divergirem no primeiro conserto.
 */
export function PlacaNoCartaz({
  children,
  /*
   * ── A TINTA DA CHAPA É UM PARÂMETRO DESDE 02/10/2026 ─────────────────────
   *
   * O carimbo do curso que **não é seu** vai em `--destaque` sobre
   * `--sobre-destaque`, e não na chapa de `--fundo`: ele é a única das quatro
   * espécies em que o cartaz leva a algum lado, e o dono pediu-o às claras —
   * *«as capas não mostram que ele não está liberado, deveria ficar de outra
   * cor»*.
   *
   * O que NÃO muda com o parâmetro é tudo o que faz a chapa ser a mesma peça: o
   * canto, o tamanho, o `tracking` e o raio. É isso que mantém «este cartaz tem
   * um estado» legível a três metros nas quatro espécies, com a cor a dizer
   * qual delas de perto.
   *
   * Em `style` próprio, e não em `resto`: o `style` literal desta função vem
   * depois do spread e apagaria qualquer um que chegasse por ali — em silêncio,
   * que é a pior maneira de um parâmetro não funcionar.
   */
  chapa = 'color-mix(in srgb, var(--fundo) 82%, transparent)',
  tinta = 'var(--texto)',
  ...resto
}: ComponentPropsWithoutRef<'span'> & { chapa?: string; tinta?: string }) {
  return (
    <span
      {...resto}
      className="absolute left-2 top-2 inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium uppercase tracking-[0.14em]"
      style={{
        background: chapa,
        color: tinta,
        borderRadius: 'calc(var(--raio) * 0.5)',
      }}
    >
      {children}
    </span>
  );
}

/**
 * ── O VÉU DO CARTAZ FECHADO (ordem do dono, 24/09/2026) ────────────────────
 *
 * > *«deveria ter um véu deixando a imagem com cores preto e branco como
 * > desativado e um cadeado ao colocar mouse em cima»* — e, logo a seguir: *«o
 * > texto de quando abre deve ficar na capa, com o véu e o cadeado, e não
 * > abaixo do título para não ocupar espaço»*.
 *
 * ## Isto DERRUBA o que estava escrito aqui, e a razão fica registada
 *
 * A entrega do cadeado de ordem dizia, em letra grossa: *«o cartaz não ganha
 * véu, nem cor, nem tarja»*, e o argumento era que a arte é do cliente e apagá-
 * la é apagar trabalho dele. O dono viu a tela pronta e decidiu o contrário —
 * *«o cartão não parece fechado o suficiente»*. Ele tem o olho no problema que
 * o argumento não cobria: **a cores cheias, o cartaz fechado lê-se como
 * qualquer outro**, e o carimbo de 10px não é visto a três metros.
 *
 * ## Vale para AS TRÊS espécies fechadas, e é por isso que é uma peça só
 *
 * Ordem, estreia e — na trilha — a etapa que espera. Três graus de fechamento
 * com três aparências de arte é o que faz o olho não aprender nenhuma: o que se
 * quer é **uma** linguagem de «desativado», e a espécie continua dita pelo
 * carimbo e pela frase, que é onde ela informa.
 *
 * ## As três camadas, e o trabalho de cada uma
 *
 * | camada | o que faz |
 * |---|---|
 * | **preto e branco** | tira a cor da arte — o sinal de três metros, e o único que sobrevive a um print |
 * | **o véu** | baixa a arte um degrau **e** carrega a frase: um véu que sustenta texto justifica-se sozinho |
 * | **o cadeado do rato** | a resposta ao gesto — «não, isto não abre» — e só ela é que aparece por hover |
 *
 * O cadeado no rato e o relógio no carimbo não competem: o carimbo é o estado
 * PERMANENTE e diz *por que* está fechado; o cadeado é a resposta a quem
 * pergunta com o rato. No toque não há hover e não há resposta — nem falta:
 * quem toca recebe o que o véu e a frase já dizem, e nenhum estado depende do
 * cadeado para se ler.
 *
 * ## O contraste, medido (e não suposto)
 *
 * A frase vai por cima de uma capa que é do cliente e pode ser branca. O véu é
 * `--fundo` a 45% em toda a arte mais um degradê até 85% na base — 91,75%
 * compostos na aresta de baixo, e **87,46% onde a frase de facto se apoia** (a
 * 15px do fundo de um cartaz de 264px, o degradê já andou 9,2% do caminho).
 *
 * | pior caso | véu resultante | contraste |
 * |---|---|---|
 * | tema de fábrica (escuro) sobre capa BRANCA | `#2a2a2d` | **13,08:1** |
 * | tema claro de cliente sobre capa PRETA | `#dfdfdf` | **14,17:1** |
 *
 * O piso é 4,5. E o controlo da conta: a mesma frase **sem véu**, sobre a capa
 * branca, dá 1,05:1 — é por isso que ela não podia simplesmente ir para cima da
 * arte. As três medições correm em `o-cartaz-fechado-parece-fechado.test.tsx`,
 * com o `packages/core/src/design/cor.ts` desta casa.
 */
export function VeuDoCartaz({
  estado,
  /*
   * ── O GLIFO DO RATO É UM PARÂMETRO DESDE 02/10/2026 ──────────────────────
   *
   * O véu ganhou um quarto inquilino — o curso que **não é seu** (`SeloSemAcesso`,
   * em `Oferta.tsx`) — e nele o cadeado estaria a mentir ao contrário: aquele
   * cartaz É um link, e leva à página de venda que o tutor declarou. O cadeado
   * é a resposta a «isto não abre»; lá a resposta é «isto abre noutro sítio».
   *
   * Um parâmetro, e não um segundo véu: as três camadas (preto e branco, a
   * chapa, a tinta da frase) e as medições de contraste do cabeçalho são as
   * mesmas nas quatro espécies — duplicá-las era como as quatro passavam a
   * divergir no primeiro conserto. O padrão continua a ser o cadeado, que é o
   * que as três espécies fechadas pedem.
   */
  gesto = <Cadeado tamanho={18} />,
}: {
  estado?: React.ReactNode;
  gesto?: React.ReactNode;
}) {
  return (
    <>
      <span
        aria-hidden
        data-vst-veu=""
        className="absolute inset-0"
        style={{
          /* Duas camadas: o degradê da base (que sustenta a frase) e, por baixo
             dele, o véu chapado que cobre a arte inteira. A cor é a do FUNDO da
             área — a mesma tinta em que o cartão está pousado —, para o cartaz
             fechado parecer que recuou para dentro da página, e não que ganhou
             uma cor nova que ninguém escolheu. */
          background:
            'linear-gradient(to top, color-mix(in srgb, var(--fundo) 85%, transparent) 0%, transparent 62%), color-mix(in srgb, var(--fundo) 45%, transparent)',
        }}
      />
      {gesto && (
        <span
          aria-hidden
          data-vst-cadeado-do-rato=""
          /* `pointer-events-none`: o cadeado não pode ser o que recebe o rato —
             ele apareceria e desapareceria por debaixo do próprio cursor. */
          className="pointer-events-none absolute inset-0 grid place-items-center opacity-0 transition-opacity duration-200 group-hover:opacity-100 motion-reduce:transition-none"
          style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
        >
          <span
            className="grid h-10 w-10 place-items-center rounded-full"
            style={{
              background: 'color-mix(in srgb, var(--fundo) 78%, transparent)',
              color: 'var(--texto)',
            }}
          >
            {gesto}
          </span>
        </span>
      )}
      {estado && (
        <div
          data-vst-estado-do-cartaz=""
          /* `--texto` e não `--texto-suave`: sobre o véu a tinta SOBE de peso, a
             mesma lei do vidro. O suave é o registo da legenda que vive sobre a
             superfície da página, não sobre a arte de alguém.

             ── O SLOT DEIXOU DE IMPOR UMA LINHA (14/10/2026) ─────────────────
             Era um `<span>` com `flex items-center`, porque o único inquilino
             era a frase da estreia — relógio e texto lado a lado. Com o motivo
             da tranca a entrar aqui (`MotivoNoCartaz`), o slot passou a ter de
             carregar um BLOCO: uma sobrancelha, uma frase de três linhas e as
             réguas. A fila que a estreia precisa mudou-se para dentro de
             `EstadoNoCartaz`, que é quem a quer; e o `<div>` é o que torna
             legal o `<p>` da frase, que um `<span>` não podia conter. */
          className="absolute inset-x-0 bottom-0 px-2.5 pb-2 pt-6 text-[11px] font-medium leading-snug"
          style={{
            color: 'var(--texto)',
            /*
             * ── A CHAPA DO SLOT, E POR QUE ELA NÃO EXISTIA ANTES ───────────
             *
             * O véu tem **altura fixa** na sua conta: o degradê da base vai de
             * 85% a transparente em 62% da arte, e a medição de 24/09 fixou o
             * ponto onde a frase se apoia — *«a 15px do fundo de um cartaz de
             * 264px»*, 87,46% compostos. Isso valia para UMA linha.
             *
             * O motivo da tranca são quatro: a sobrancelha, até três linhas de
             * frase e duas réguas. O topo dele sobe a ~29% da altura da arte,
             * onde o degradê já andou quase todo o caminho — e lá o composto
             * cai para 69,9%, que põe a sobrancelha em **2,92:1** sobre uma
             * capa branca. Abaixo do piso de 4,5, e no elemento que esta obra
             * existe para tornar visível.
             *
             * A chapa resolve-o pela única via que não depende da altura: ela é
             * do SLOT, logo acompanha o que o slot tiver dentro.
             *
             * **A banda do fade é de 24px, e não de 24%** — e isso é um retrato,
             * não uma preferência. Em percentagem ela cresce com o slot: num
             * motivo de 178px (o caso do telemóvel, com duas exigências
             * pendentes) os 24% dão 43px, e a sobrancelha, que o `pt-6` põe a
             * 24px do topo, pousava DENTRO do esmaecido — fotografado a 390px,
             * com o monograma a atravessar a palavra. Em pixels, a banda acaba
             * exactamente onde o padding acaba, em qualquer altura de slot: a
             * primeira linha de tinta assenta sempre nos 90% cheios, que é o
             * número que a cerca do contraste mede.
             *
             * `--fundo`, e não uma cor nova: é a mesma tinta do véu, pela mesma
             * razão escrita lá em cima. O que muda é a densidade.
             *
             * Medido em `o-cartaz-fechado-parece-fechado`, com o
             * `packages/core/src/design/cor.ts` desta casa:
             *
             * | pior caso | véu + chapa | contraste |
             * |---|---|---|
             * | fábrica (escuro) sobre capa BRANCA | `#18181c` | **6,88:1** |
             * | claro de cliente sobre capa PRETA | `#f1f1f1` | **6,84:1** |
             *
             * E o controlo que a justifica: o mesmo ponto **sem** a chapa dá
             * `#545457` e **2,92:1**.
             */
            background:
              'linear-gradient(to top, color-mix(in srgb, var(--fundo) 90%, transparent) 0, color-mix(in srgb, var(--fundo) 90%, transparent) calc(100% - 24px), transparent 100%)',
          }}
        >
          {estado}
        </div>
      )}
    </>
  );
}

/** O carimbo de quem espera OUTRO CONTEÚDO: cadeado, e a palavra do estado. */
export function SeloDeOrdem() {
  const t = useT();
  return (
    <PlacaNoCartaz data-vst-tranca="selo">
      <Cadeado />
      {t('acesso.trancado')}
    </PlacaNoCartaz>
  );
}

/**
 * A régua de UMA exigência.
 *
 * A marca do mínimo só existe abaixo de 100: quando o tutor pede o conteúdo
 * inteiro, o mínimo **é o fim da barra**, e um traço em cima da borda direita
 * seria um pixel a dizer o que a forma já diz.
 *
 * O trilho é `--texto-suave` a 18%, o mesmo da barra da etapa, para as duas
 * réguas da mesma tela pesarem igual.
 */
function Regua({
  medida,
  /**
   * ── A MESMA RÉGUA, COM A PELE DE QUEM ESTÁ SOBRE A ARTE (14/10/2026) ─────
   *
   * O argumento que mantinha o motivo fora da capa dizia, com razão, que *«uma
   * régua de 3px sobre uma fotografia é uma medida que ninguém lê»*. Ele
   * descrevia **esta** régua — a da superfície da página: 3px, trilho a 18% de
   * `--texto-suave`, números em `--texto-suave`, e um rótulo preso a 42px que
   * come metade da largura de um cartão de 150px.
   *
   * Sobre a arte ela engorda para 5px, arredonda, o trilho sobe para 26% de
   * `--texto` (sobre o véu, que é `--fundo` a 85% na base) e os números passam
   * a `--texto`. O rótulo sai da coluna fixa e vai colado ao número, onde ele
   * gasta a largura que tem e não a que foi reservada.
   *
   * **Um parâmetro, e não uma segunda régua.** As duas vivem a 8px da mesma
   * decisão — a marca do mínimo, o fraco da cumprida, o par `atual/mínimo` — e
   * duplicá-las é como elas divergem no primeiro conserto. É a mesma lei que
   * `VeuDoCartaz` já aplica ao glifo e `PlacaNoCartaz` à tinta da chapa.
   */
  sobreArte = false,
}: {
  medida: MedidaDaTranca;
  sobreArte?: boolean;
}) {
  const tintaDoNumero = sobreArte ? 'var(--texto)' : 'var(--texto-suave)';
  return (
    <div className="flex items-center gap-2" data-vst-regua={medida.rotulo}>
      {!sobreArte && (
        <span
          className="w-[42px] shrink-0 text-[10px] uppercase tracking-[0.12em]"
          style={{ color: 'var(--texto-suave)' }}
        >
          {medida.rotulo}
        </span>
      )}
      <span
        className={sobreArte ? 'relative h-[5px] flex-1 rounded-full' : 'relative h-[3px] flex-1'}
        style={{
          background: sobreArte
            ? 'color-mix(in srgb, var(--texto) 26%, transparent)'
            : 'color-mix(in srgb, var(--texto-suave) 18%, transparent)',
        }}
        aria-hidden
      >
        <span
          className={
            sobreArte ? 'absolute inset-y-0 left-0 rounded-full' : 'absolute inset-y-0 left-0'
          }
          style={{
            width: `${medida.atual}%`,
            /* Cumprida fica mais fraca de propósito: a régua que ainda pede
               alguma coisa é a que tem de puxar o olho primeiro. */
            background: medida.cumprida
              ? 'color-mix(in srgb, var(--destaque) 45%, transparent)'
              : 'var(--destaque)',
          }}
        />
        {medida.minimo < 100 && (
          <span
            className={
              sobreArte
                ? 'absolute -top-[2px] -bottom-[2px] w-[2px] rounded-full'
                : 'absolute -top-[3px] -bottom-[3px] w-px'
            }
            style={{ left: `${medida.minimo}%`, background: 'var(--texto)' }}
          />
        )}
      </span>
      <span className="shrink-0 text-[11px] tabular-nums" style={{ color: tintaDoNumero }}>
        {sobreArte && (
          <span className="tracking-[0.06em]" style={{ color: 'var(--texto-suave)' }}>
            {medida.rotulo}{' '}
          </span>
        )}
        {medida.atual}/{medida.minimo}
      </span>
    </div>
  );
}

/**
 * Só as réguas — para a tela que já escreveu a frase por conta própria.
 *
 * Existe por causa da RECUSA da aula (o 403): ali a frase é o título da tela,
 * em 18px, e chega pronta do servidor. Desenhar `MotivoDaTranca` por cima
 * escreveria a mesma frase duas vezes, a segunda mais pequena — e fazer a
 * recusa redigir a sua régua seria a segunda implementação da mesma peça.
 */
export function ReguasDaTranca({
  tranca,
  sobreArte = false,
}: {
  tranca: TrancaDeOrdem;
  sobreArte?: boolean;
}) {
  const medidas = medidasDaTranca(tranca);
  if (medidas.length === 0) return null;
  return (
    <div className={sobreArte ? 'mt-1.5 space-y-1' : 'mt-2 space-y-1.5'}>
      {medidas.map((m) => (
        <Regua key={m.rotulo} medida={m} sobreArte={sobreArte} />
      ))}
    </div>
  );
}

/**
 * O motivo inteiro: a frase, o nome que leva ao que falta, e as réguas.
 *
 * `tranca: null` é o cadeado sem motivo — acontece quando a porta está fechada
 * e o campo não chegou (corpo antigo). Aí diz-se que depende de algo **sem
 * nomear nada**: cadeado mudo reprova, cadeado a adivinhar mente.
 */
export function MotivoDaTranca({
  tranca,
  areaSlug,
  /**
   * O cartão da prateleira é estreito — 150px no telemóvel — e lá a frase
   * inteira a 14px vira sete linhas. `compacto` desce-a para os 12px da linha
   * de legenda que já vive por baixo de cada cartaz («8 aulas»), que é o
   * registo daquele lugar. Medido em 390px no retrato de 23/09.
   */
  compacto = false,
  /**
   * A régua sai onde o objeto já tem uma barra **que é a identidade dele**.
   *
   * É o caso do ladrilho de «continue de onde parou»: o trilho na base dele é
   * metade da distinção entre «retomar» e «seguir», e não pode sair. Duas
   * barras a 8px uma da outra, uma sobre a aula e outra sobre a dependência,
   * é a leitura que este desenho recusa em todo o lado — e onde não se pode
   * tirar a do objeto, tira-se a da tranca. A frase continua a dizer os dois
   * números por extenso, que é o que a régua desenha.
   */
  reguas = true,
  /** Ver `Frase`. `false` no diálogo, onde a fila de ações é que leva lá. */
  link = true,
}: {
  tranca: TrancaDeOrdem | null;
  areaSlug: string;
  compacto?: boolean;
  reguas?: boolean;
  link?: boolean;
}) {
  const corpo = compacto ? 'text-xs leading-snug' : 'text-sm leading-snug';

  if (!tranca) {
    return (
      <p className={corpo} style={{ color: 'var(--texto-suave)' }} data-vst-tranca="motivo">
        {SEM_MOTIVO}
      </p>
    );
  }

  return (
    <div className="min-w-0" data-vst-tranca="motivo">
      <Frase
        tranca={tranca}
        areaSlug={areaSlug}
        className={corpo}
        tinta="var(--texto-suave)"
        peso=""
        link={link}
      />
      {reguas && <ReguasDaTranca tranca={tranca} />}
    </div>
  );
}

/**
 * A frase com o nome do que falta dentro dela, e o nome é o link.
 *
 * Uma implementação para os dois lugares — a superfície da página e a capa —
 * porque o que muda entre eles é a TINTA e o CORPO, nunca a redação nem o que
 * vira link. Escrita duas vezes, a capa seria o sítio onde o nome deixaria de
 * levar a lado nenhum no primeiro conserto, e ninguém daria por isso: é o
 * cartão que a pessoa não consegue abrir.
 */
function Frase({
  tranca,
  areaSlug,
  className,
  tinta,
  peso,
  /**
   * O nome vira link? Sempre, menos onde quem recebe o gesto é outra coisa.
   *
   * Dois sítios dizem `false`, e pela MESMA razão: ali a frase está dentro de
   * um elemento que já é interativo — a capa da prateleira, que é um `<button>`
   * desde 14/10/2026, e o `AvisoDaTranca` que esse botão abre, onde o alvo é o
   * botão da fila de ações. Um `<a>` dentro de um `<button>` é nesting ilegal;
   * e um link na prosa a competir com o botão a 8px dele é o alvo que se
   * alcança por acidente — o argumento de 24/09, que aqui passa a ser cumprido
   * em vez de contornado.
   *
   * Um parâmetro, e não uma segunda frase: a redação é do core (R5-bis), e o
   * que muda entre os leitores nunca é uma palavra dela.
   */
  link = true,
}: {
  tranca: TrancaDeOrdem;
  areaSlug: string;
  className: string;
  tinta: string;
  peso: string;
  link?: boolean;
}) {
  const { antes, titulo, depois } = partesDaFrase(tranca);
  const destino = link ? destinoDaTranca(tranca) : null;
  return (
    <p className={className} style={{ color: tinta }}>
      {antes}
      {titulo !== null && (
        <>
          «
          {destino ? (
            <Link
              to={caminhoDaArea(areaSlug, destino)}
              className={`underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${peso}`}
              style={{ color: 'var(--texto)', outlineColor: 'var(--destaque)' }}
            >
              {titulo}
            </Link>
          ) : (
            <span className={peso} style={{ color: 'var(--texto)' }}>
              {titulo}
            </span>
          )}
          »
        </>
      )}
      {depois}
    </p>
  );
}

/**
 * ── O MOTIVO DENTRO DA CAPA (ordem do dono, 14/10/2026) ─────────────────────
 *
 * > *«eu quero que a mensagem de dependência de outros cursos apareça na
 * > própria capa do curso, e não no rodapé da capa … perceba que essa mensagem
 * > no rodapé da capa acaba desnivelando as demais capas da prateleira. e o
 * > usuário por ter fonte pequena por vezes não percebe o motivo do bloqueio»*
 *
 * ## Isto DERRUBA a decisão de 24/09, e a razão fica registada
 *
 * Quando a frase da estreia se mudou para dentro da capa, a da tranca ficou
 * fora **de propósito**, com dois argumentos escritos em `CapaDeCurso`:
 *
 * 1. *«um link dentro de um véu sobre arte é um alvo que se alcança por
 *    acidente»*;
 * 2. *«uma régua de 3px sobre uma fotografia é uma medida que ninguém lê»*.
 *
 * O dono viu a prateleira pronta e mediu o que os dois argumentos não cobriam:
 * o cartão trancado é **o único da fileira com três linhas debaixo do título**,
 * e por isso é ele que desnivela a prateleira inteira — exactamente o defeito
 * que a mudança da estreia tinha vindo corrigir, deixado por resolver na
 * espécie vizinha. E a frase, em 12px `--texto-suave` num rodapé, não era lida:
 * o cadeado via-se, o motivo não.
 *
 * ## O que esta peça faz com os dois argumentos, em vez de os ignorar
 *
 * | argumento de 24/09 | o que mudou aqui |
 * |---|---|
 * | o link por acidente | **resolvido no fim do mesmo dia, e por cima**: o nome deixou de ser link, e o cartaz inteiro passou a ser o gesto — ver abaixo |
 * | a régua de 3px | sobre a arte ela é de **5px**, arredondada, com o trilho a 26% de `--texto` e o par `avanço 7/80` em `--texto` — ver `Regua`, `sobreArte` |
 *
 * ## O NOME NÃO É O LINK AQUI (segunda ordem do dono, 14/10/2026, à tarde)
 *
 * > *«eu quero que ao clicar em uma capa com dependência de outro curso,
 * > apareça uma mensagem avisando sobre isso, como um modal»*
 *
 * A capa trancada passou a ser um `<button>` que abre `AvisoDaTranca`. Isso
 * muda duas coisas nesta peça, e as duas para melhor:
 *
 * 1. **a frase vai com `link={false}`** — um `<a>` dentro de um `<button>` é
 *    nesting ilegal, e o clique no nome disputaria o clique do cartão;
 * 2. **o argumento de 24/09 passa a ser cumprido** — *«um link dentro de um
 *    véu sobre arte é um alvo que se alcança por acidente»*. Deixou de haver
 *    alvo de 13px sobre a arte: há um alvo do tamanho do cartaz, e o que ele
 *    entrega é a EXPLICAÇÃO. A saída para o pré-requisito continua a existir,
 *    um toque mais funda, num botão de 44px que nomeia o que falta.
 *
 * O nome fica a `font-semibold` em `--texto`, e **sem sublinhado** — ele vem do
 * ramo não-link da `Frase`: sublinhar o que não leva a sítio nenhum é a mentira
 * que o cadeado decorativo faz ao contrário.
 *
 * ## As três linhas, e o trabalho de cada uma
 *
 * | linha | o que faz |
 * |---|---|
 * | **PARA ABRIR** | a sobrancelha de 10px: diz o que se vai ler antes de se ler, e é o que o carimbo do canto não consegue dizer |
 * | **a frase** | 13px em `--texto` — o degrau que o dono pediu, contra os 12px `--texto-suave` do rodapé |
 * | **as réguas** | a distância desenhada, que é o que transforma a recusa num percurso |
 *
 * A frase vem de `partesDaFrase`, do core, **sem uma palavra mudada**: são seis
 * leitores a dizer a mesma coisa (R5-bis), e a capa é o sétimo, não o primeiro
 * a redigi-la por conta própria.
 *
 * ## `line-clamp-4`, e o que acontece na linha que ele corta
 *
 * Medido a 390px, onde a capa fica no mínimo da `clamp` (150px): a frase de UMA
 * exigência — *«O curso «X» pede 80% — você está em 7%.»* — ocupa **4 linhas**,
 * e é por isso que o teto é 4 e não 3. Com `line-clamp-3` ela cortava em *«você
 * está em…»*, isto é, **exactamente no número que esta obra existe para tornar
 * visível**; está fotografado.
 *
 * Com as DUAS exigências pendentes ela chega às seis, e aí o teto corta. O que
 * não se perde é o fato: as réguas imediatamente abaixo dizem `avanço 0/80` e
 * `nota 55/70` por extenso, que são os quatro números da frase. É a mesma
 * divisão de trabalho que o core já escreve — *«a frase diz o próximo gesto; a
 * régua mostra o quadro completo»* —, e é ela que faz o corte ser aceitável
 * aqui e não o ter sido na linha de cima.
 */
export function MotivoNoCartaz({ tranca, areaSlug }: { tranca: TrancaDeOrdem; areaSlug: string }) {
  const t = useT();
  return (
    <div className="min-w-0" data-vst-tranca="motivo">
      <span
        data-vst-tranca="sobrancelha"
        className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.14em]"
        style={{ color: 'var(--texto-suave)' }}
      >
        {t('acesso.paraAbrir')}
      </span>
      <Frase
        tranca={tranca}
        areaSlug={areaSlug}
        className="line-clamp-4 text-[13px] font-normal leading-snug"
        tinta="var(--texto)"
        peso="font-semibold"
        link={false}
      />
      <ReguasDaTranca tranca={tranca} sobreArte />
    </div>
  );
}

/**
 * ── O AVISO AO CLICAR NA CAPA TRANCADA (ordem do dono, 14/10/2026) ──────────
 *
 * > *«eu quero que ao clicar em uma capa com dependência de outro curso,
 * > apareça uma mensagem avisando sobre isso, como um modal»*
 *
 * ## O defeito que isto fecha
 *
 * Até aqui o cartaz trancado **não respondia ao clique** — era um `<div>`, por
 * uma razão boa e que continua a valer: o servidor recusa o corpo (`exigirAula`
 * devolve 403), e um `<Link>` para `/c/:id` seria a tela a oferecer o que a API
 * fecha. Mas «não ser um link» virou «não ser nada»: a pessoa toca no cartaz,
 * **nada acontece**, e a tela não distingue *«isto não abre e eu explico por
 * quê»* de *«isto está quebrado»*. O silêncio no gesto primário é a terceira
 * forma do mesmo defeito que esta casa já pagou duas vezes — o atalho que
 * apaga a explicação — e é a pior delas, porque não deixa rasto nenhum.
 *
 * Agora o gesto primário entrega **exactamente** o que o cadeado tem para dar:
 * o motivo, por extenso, no corpo de leitura, com as réguas por inteiro — e o
 * caminho para o que falta, se ele existir.
 *
 * ## Por que um diálogo, e não a navegação para o pré-requisito
 *
 * Porque levar direto ao pré-requisito é o atalho que apaga a explicação: a
 * pessoa clica em «AutoNext» e aterra em «Pro N8N», sem nunca ler que um pede
 * o outro. Está medido nesta casa e tem memória própria. O diálogo inverte a
 * ordem — primeiro o porquê, depois a porta — e deixa a porta a UM toque.
 *
 * ## A gramática é a da casa, e nenhum token é novo
 *
 * O véu é o de `Parabens.tsx` e de `ConfirmacaoDaProva.tsx`, letra por letra —
 * **o fundo da área a 92%**, que funciona nos dois temas porque é o próprio
 * fundo da página, quase opaco. `role="dialog"`, `aria-modal`, o título ligado
 * por `aria-labelledby`, foco que entra ao abrir e **volta ao cartaz** ao sair,
 * `Esc` e o fundo a executar a saída segura, e o laço do `Tab` para o foco não
 * cair na prateleira que ficou atrás.
 *
 * ## Por que NÃO vai num portal, e isto é medição de outra sessão
 *
 * Porque portar para o `<body>` **perde o tema**: as variáveis vivem no `<div
 * style={variaveisDoTema(…)}>` de cada rota e herdam pela árvore do DOM — fora
 * dela, `var(--superficie)` vira declaração inválida e o diálogo abre
 * transparente. Está escrito em `busca/Painel.tsx`, que o aprendeu com a porta
 * do tutor. Aqui o `position: fixed` chega sozinho: a prateleira é
 * `overflow-x-auto` **sem** `transform` nem `backdrop-filter`, e é só isso que
 * criaria bloco de contenção. Quem puser um filtro no trilho da prateleira
 * parte este diálogo — e é por isso que a razão está escrita, e não suposta.
 *
 * ## A primeira linha é a MESMA da página do curso
 *
 * «Este curso ainda não abriu.» — a palavra por palavra de
 * `AvisoDoCursoTrancado`, que é a tela onde esta pessoa aterra se entrar por
 * link direto. Duas telas que dizem a mesma coisa dizem-na com as mesmas
 * palavras; a espécie troca para «Esta trilha» porque uma trilha não é um
 * curso, e essa é a única variação.
 *
 * E **não há sobrancelha «Trancado»** por cima dela, pela razão que a página do
 * curso já escreveu: seria o mesmo fato duas vezes a 8px de distância. O
 * cadeado ao lado dá o sinal de relance; a frase dá o fato.
 */
export function AvisoDaTranca({
  especie,
  tranca,
  areaSlug,
  aoFechar,
}: {
  especie: 'curso' | 'trilha';
  tranca: TrancaDeOrdem;
  areaSlug: string;
  aoFechar: () => void;
}) {
  const t = useT();
  const caixa = useRef<HTMLDivElement>(null);
  const devolverFoco = useRef<Element | null>(null);
  const destino = destinoDaTranca(tranca);

  useEffect(() => {
    devolverFoco.current = document.activeElement;
    /* O foco entra na CAIXA, e não num botão — a mesma escolha de `Parabens`, e
       pela mesma razão: este diálogo é uma explicação, e quem o ouve tem de
       receber a explicação, não o nome do botão de sair. O `Tab` seguinte cai
       na fila de ações, que é onde há o que fazer. */
    caixa.current?.focus();
    return () => {
      /* E volta ao cartaz que o abriu. Sem isto, fechar pelo teclado deixa o
         foco no início do documento e a pessoa perde a prateleira onde estava. */
      const antes = devolverFoco.current;
      if (antes instanceof HTMLElement) antes.focus();
    };
  }, []);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        aoFechar();
        return;
      }
      if (e.key !== 'Tab') return;
      /* `a[href]` entra no laço, e é a diferença desta caixa para a da prova:
         lá as ações são todas botões, aqui a ação forte é um `<Link>` — porque
         é uma navegação, e uma navegação que não se pode abrir noutra aba é uma
         navegação disfarçada de botão. */
      const focaveis = caixa.current?.querySelectorAll<HTMLElement>('button, a[href]');
      if (!focaveis || focaveis.length === 0) return;
      const primeiro = focaveis[0]!;
      const ultimo = focaveis[focaveis.length - 1]!;
      if (e.shiftKey && document.activeElement === primeiro) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primeiro.focus();
      }
    };
    document.addEventListener('keydown', aoTeclar, true);
    return () => document.removeEventListener('keydown', aoTeclar, true);
  }, [aoFechar]);

  return (
    <div
      className="vst-fundo fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'color-mix(in srgb, var(--fundo) 92%, transparent)' }}
      onMouseDown={(e) => {
        /* Só o fundo, e `mousedown` e não `click`: arrastar de dentro para fora
           ao selecionar a frase fecharia o diálogo no `click`. */
        if (e.target === e.currentTarget) aoFechar();
      }}
    >
      <div
        ref={caixa}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vst-tranca-aviso-titulo"
        data-vst-tranca="aviso"
        className="vst-entra flex w-full max-w-md flex-col gap-4 p-6 text-left outline-none"
        style={{
          background: 'var(--superficie)',
          color: 'var(--texto)',
          border: '1px solid color-mix(in srgb, var(--texto-suave) 30%, transparent)',
          borderRadius: 'var(--raio)',
        }}
      >
        <p id="vst-tranca-aviso-titulo" className="flex items-start gap-2 text-lg font-medium">
          <span className="mt-[5px] shrink-0" style={{ color: 'var(--texto-suave)' }}>
            <Cadeado tamanho={14} />
          </span>
          {especie === 'trilha' ? t('estado.trilhaNaoAbriu') : t('estado.cursoNaoAbriu')}
        </p>
        {/* O mesmo recuo de 22px da página do curso — a frase alinha com a
            linha grande, e não com o cadeado. */}
        <div className="pl-[22px]">
          {/*
           * `compacto` NÃO: é aqui que a frase ganha o corpo de leitura que ela
           * não tem na capa, e é essa a metade da ordem do dono que fala de
           * *«fonte pequena»*. E a régua é a da superfície — 3px, com o rótulo
           * na coluna dele —, porque esta caixa é superfície de página e não
           * arte de ninguém: `sobreArte` existe para o véu, que aqui não há.
           */}
          <MotivoDaTranca tranca={tranca} areaSlug={areaSlug} link={false} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {destino && (
            /*
             * A ação forte é um `<Link>` com cara de botão — 44px de altura,
             * que é o piso de toque, e não os 16px de um sublinhado no meio de
             * uma oração.
             *
             * **E ela NOMEIA o que falta**, contra a regra de não escrever o
             * mesmo nome duas vezes a 8px. A regra foi escrita para o cartão, e
             * aqui ela cederia a coisa errada: nesta caixa há DOIS cursos
             * nomeados — o que a pessoa clicou e o que ele pede —, e «Ir para o
             * curso» não diz qual. Entre repetir um nome e ser ambíguo numa
             * porta, repete-se o nome.
             */
            <Link
              to={caminhoDaArea(areaSlug, destino)}
              data-vst-tranca="saida"
              className="inline-flex min-h-[44px] items-center px-5 py-2.5 text-sm font-medium"
              style={{
                background: 'var(--destaque)',
                color: 'var(--sobre-destaque)',
                borderRadius: 'var(--raio)',
              }}
            >
              {t('tranca.irPara', { titulo: tranca.titulo })}
            </Link>
          )}
          {/*
           * «Fechar», e não «Entendi»: o gesto de fechar tem de significar
           * exactamente o que o botão quieto diz, e esta caixa não pede
           * concordância nenhuma — ela informa. É a mesma lei da saída segura
           * de `ConfirmacaoDaProva`, e é ela que `Esc` e o fundo executam.
           *
           * Sem destino (um módulo ou uma trilha exigidos, que não têm rota no
           * app do aluno) este botão fica sozinho — e a caixa continua a valer
           * a abertura, porque o que ela entrega é o motivo.
           */}
          <button
            type="button"
            data-saida-segura=""
            onClick={aoFechar}
            className="min-h-[44px] cursor-pointer px-5 py-2.5 text-sm font-medium"
            style={{
              border: '1px solid var(--texto-suave)',
              color: 'var(--texto)',
              borderRadius: 'var(--raio)',
            }}
          >
            {t('geral.fechar')}
          </button>
        </div>
      </div>
    </div>
  );
}
